import React from 'react';
import type { VibeBlockDesignWizardOptionType } from '../api.types';

type VibeBlockDesignWizardOptionArtworkProps = {
  option: VibeBlockDesignWizardOptionType;
  optionIndex?: number;
  step?: number;
};

export type MascotMotionResult = {
  type:
    | 'spin-burst'
    | 'spin-jump'
    | 'hop'
    | 'tilt'
    | 'aura'
    | 'cursor-hover'
    | 'tap-ripple'
    | 'speed-dash'
    | 'scroll-reveal'
    | 'float-smooth'
    | 'float';
  className: string;
};

const FORBIDDEN_SVG_ELEMENTS = new Set([
  'a',
  'audio',
  'embed',
  'foreignobject',
  'iframe',
  'image',
  'object',
  'script',
  'use',
  'video'
]);

export function sanitizeWizardSvg(rawSvg: string): string | null {
  const trimmed = rawSvg.trim();
  if (!trimmed.startsWith('<svg') || !trimmed.endsWith('</svg>')) {
    return null;
  }

  const document = new DOMParser().parseFromString(trimmed, 'image/svg+xml');
  const root = document.documentElement;
  if (root.localName.toLowerCase() !== 'svg' || document.querySelector('parsererror')) {
    return null;
  }

  Array.from(root.querySelectorAll('*')).forEach((element) => {
    if (FORBIDDEN_SVG_ELEMENTS.has(element.localName.toLowerCase())) {
      element.remove();
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value;
      if (
        name.startsWith('on')
        || name === 'href'
        || name === 'src'
        || name === 'xlink:href'
        || /javascript:|data:|url\s*\(|@import/i.test(value)
      ) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.localName.toLowerCase() === 'style' && /url\s*\(|@import/i.test(element.textContent || '')) {
      element.remove();
    }
  });

  Array.from(root.attributes).forEach((attribute) => {
    const name = attribute.name.toLowerCase();
    if (
      name.startsWith('on')
      || name === 'href'
      || name === 'src'
      || name === 'xlink:href'
      || /javascript:|data:|url\s*\(|@import/i.test(attribute.value)
    ) {
      root.removeAttribute(attribute.name);
    }
  });

  return new XMLSerializer().serializeToString(root);
}

export function resolveMascotMotion(
  option: VibeBlockDesignWizardOptionType,
  optionIndex = 0,
  step = 1
): MascotMotionResult {
  const text = `${option.title} ${option.explanation} ${option.business_effect || ''} ${option.preview?.motion || ''}`.toLowerCase();

  // 1. Interactive Cursor / Hover / Mouse reaction
  if (
    text.includes('ховер') ||
    text.includes('курсор') ||
    text.includes('наведенн') ||
    text.includes('мишк') ||
    text.includes('під курсором') ||
    text.includes('hover') ||
    text.includes('cursor') ||
    text.includes('pointer') ||
    option.preview?.motion === 'tilt'
  ) {
    return { type: 'cursor-hover', className: 'vibe-block-art-mascot--cursor-hover' };
  }

  // 2. Click / Tap / Interactive trigger
  if (
    text.includes('клік') ||
    text.includes('натиск') ||
    text.includes('тап') ||
    text.includes('взаємоді') ||
    text.includes('тригер') ||
    text.includes('click') ||
    text.includes('tap') ||
    text.includes('press') ||
    text.includes('trigger')
  ) {
    return { type: 'tap-ripple', className: 'vibe-block-art-mascot--tap-ripple' };
  }

  // 3. Scroll / Reveal
  if (
    text.includes('скрол') ||
    text.includes('прокрут') ||
    text.includes('появ') ||
    text.includes('scroll') ||
    text.includes('reveal')
  ) {
    return { type: 'scroll-reveal', className: 'vibe-block-art-mascot--scroll-reveal' };
  }

  // 4. Aura / Glow / Neon / Ambient Lighting
  if (
    text.includes('аур') ||
    text.includes('сяйв') ||
    text.includes('неон') ||
    text.includes('світл') ||
    text.includes('підсвіт') ||
    text.includes('ореол') ||
    text.includes('glow') ||
    text.includes('aura') ||
    text.includes('halo') ||
    text.includes('neon') ||
    text.includes('ambient')
  ) {
    return { type: 'aura', className: 'vibe-block-art-mascot--aura' };
  }

  // 5. Double spin with sparks / glow burst / stars
  if (
    text.includes('подвійн') ||
    text.includes('два швидк') ||
    text.includes('іскр') ||
    text.includes('зір') ||
    text.includes('зірочк') ||
    text.includes('спалах') ||
    text.includes('блиск') ||
    text.includes('салют') ||
    text.includes('магі') ||
    text.includes('burst') ||
    text.includes('double') ||
    text.includes('sparkle') ||
    text.includes('flash') ||
    text.includes('star') ||
    text.includes('magic')
  ) {
    return { type: 'spin-burst', className: 'vibe-block-art-mascot--spin-burst' };
  }

  // 6. Speed / Kinetic / Dash / Fast
  if (
    text.includes('швидк') ||
    text.includes('динамі') ||
    text.includes('енергій') ||
    text.includes('драйв') ||
    text.includes('стрімк') ||
    text.includes('активн') ||
    text.includes('fast') ||
    text.includes('speed') ||
    text.includes('dash') ||
    text.includes('kinetic') ||
    text.includes('quick')
  ) {
    return { type: 'speed-dash', className: 'vibe-block-art-mascot--speed-dash' };
  }

  // 7. Hop / Jump straight up without spin
  if (
    text.includes('без оберт') ||
    text.includes('без крутін') ||
    text.includes('просто підстриб') ||
    (text.includes('стрибок') && !text.includes('пірует') && !text.includes('оберт')) ||
    text.includes('підстриб') ||
    text.includes('баунс') ||
    text.includes('hop') ||
    text.includes('bounce')
  ) {
    return { type: 'hop', className: 'vibe-block-art-mascot--hop' };
  }

  // 8. Pirouette / Spin + Jump
  if (
    text.includes('пірует') ||
    text.includes('оберт') ||
    text.includes('крутін') ||
    text.includes('поворот') ||
    text.includes('сальто') ||
    text.includes('spin') ||
    text.includes('pirouette') ||
    text.includes('twist') ||
    text.includes('rotate') ||
    option.preview?.motion === 'spin'
  ) {
    return { type: 'spin-jump', className: 'vibe-block-art-mascot--spin-jump' };
  }

  // 9. Tilt / curious sway
  if (
    text.includes('нахил') ||
    text.includes('похит') ||
    text.includes('погляд') ||
    text.includes('tilt')
  ) {
    return { type: 'tilt', className: 'vibe-block-art-mascot--tilt' };
  }

  // 10. Smooth / Gentle / Drift
  if (
    text.includes('плавн') ||
    text.includes('спокійн') ||
    text.includes('м\'як') ||
    text.includes('повільн') ||
    text.includes('ніжн') ||
    text.includes('smooth') ||
    text.includes('gentle') ||
    text.includes('calm') ||
    text.includes('slow') ||
    text.includes('drift')
  ) {
    return { type: 'float-smooth', className: 'vibe-block-art-mascot--float-smooth' };
  }

  // 11. STEP & OPTION INDEX CONTEXTUAL FALLBACK
  // Guarantees that even with generic labels, EVERY option on EVERY step has a unique, beautiful animation!
  const safeIndex = optionIndex % 3;

  if (step === 1) {
    if (safeIndex === 0) return { type: 'spin-jump', className: 'vibe-block-art-mascot--spin-jump' };
    if (safeIndex === 1) return { type: 'hop', className: 'vibe-block-art-mascot--hop' };
    return { type: 'spin-burst', className: 'vibe-block-art-mascot--spin-burst' };
  }

  if (step === 2) {
    if (safeIndex === 0) return { type: 'aura', className: 'vibe-block-art-mascot--aura' };
    if (safeIndex === 1) return { type: 'cursor-hover', className: 'vibe-block-art-mascot--cursor-hover' };
    return { type: 'spin-burst', className: 'vibe-block-art-mascot--spin-burst' };
  }

  // Step 3 or default:
  if (safeIndex === 0) return { type: 'speed-dash', className: 'vibe-block-art-mascot--speed-dash' };
  if (safeIndex === 1) return { type: 'float-smooth', className: 'vibe-block-art-mascot--float-smooth' };
  return { type: 'tap-ripple', className: 'vibe-block-art-mascot--tap-ripple' };
}

export const VibeBlockDesignWizardOptionArtwork: React.FC<VibeBlockDesignWizardOptionArtworkProps> = ({
  option,
  optionIndex = 0,
  step = 1
}) => {
  const { preview } = option;

  // 1. Custom AI-generated SVG if provided
  if (preview?.svg && typeof preview.svg === 'string') {
    const cleanSvg = sanitizeWizardSvg(preview.svg);
    if (cleanSvg) {
      return (
        <div
          className="vibe-block-design-wizard-option-art-custom"
          dangerouslySetInnerHTML={{ __html: cleanSvg }}
          aria-hidden="true"
        />
      );
    }
  }

  const motif = preview?.motif || 'content';
  const motion = preview?.motion || 'none';
  const layout = preview?.layout || 'split';
  const safeIndex = optionIndex % 3;

  // Choose the visual archetype based on motif, motion, layout, step, and optionIndex
  if (motif === 'character') {
    const mascotMotion = resolveMascotMotion(option, optionIndex, step);

    return (
      <svg
        className={`vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--character vibe-block-design-wizard-option-art--char-${mascotMotion.type}`}
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="char-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="burst-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fef08a" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#f472b6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="char-body" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="50%" stopColor="#e0e7ff" />
            <stop offset="100%" stopColor="#a5b4fc" />
          </linearGradient>
          <linearGradient id="char-ear" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f472b6" />
            <stop offset="100%" stopColor="#c084fc" />
          </linearGradient>
        </defs>

        {/* Ambient background glow */}
        <circle cx="58" cy="38" r="32" fill="url(#char-glow)" />

        {/* 1. Sparkle burst element for double spin / sparkle option */}
        {mascotMotion.type === 'spin-burst' && (
          <g className="vibe-block-art-sparkle-burst">
            <circle cx="59" cy="36" r="28" fill="url(#burst-halo)" />
            <path d="M59 14 L61.5 28 L75 30 L63 35 L67 49 L55 40 L43 46 L48 33 L36 29 L50 27 Z" fill="#fef08a" opacity="0.95" />
            <circle cx="30" cy="22" r="2.8" fill="#67e8f9" />
            <circle cx="88" cy="20" r="3.2" fill="#f472b6" />
            <circle cx="92" cy="48" r="2.8" fill="#fef08a" />
            <circle cx="26" cy="50" r="2.8" fill="#c084fc" />
            <circle cx="59" cy="54" r="2" fill="#67e8f9" />
          </g>
        )}

        {/* 2. Concentric aura rings for glow / neon / ambient */}
        {mascotMotion.type === 'aura' && (
          <g className="vibe-block-art-aura-rings">
            <circle cx="59" cy="38" r="24" stroke="#818cf8" strokeWidth="1.5" opacity="0.65" strokeDasharray="4 2" />
            <circle cx="59" cy="38" r="30" stroke="#c084fc" strokeWidth="1" opacity="0.45" />
            <circle cx="59" cy="38" r="36" stroke="#67e8f9" strokeWidth="0.8" opacity="0.3" strokeDasharray="3 3" />
          </g>
        )}

        {/* 3. Interactive pointer cursor for hover */}
        {mascotMotion.type === 'cursor-hover' && (
          <g className="vibe-block-art-cursor-pointer">
            <path
              d="M74 38 L68 26 L80 29 L76 34 L81 39 L78 42 L73 37 Z"
              fill="#ffffff"
              stroke="#4f46e5"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </g>
        )}

        {/* 4. Tap ripple for click/interactive */}
        {mascotMotion.type === 'tap-ripple' && (
          <g className="vibe-block-art-tap-ripple-group">
            <circle cx="59" cy="40" r="14" stroke="#6366f1" strokeWidth="1.5" fill="rgba(99, 102, 241, 0.15)" />
            <circle cx="59" cy="40" r="22" stroke="#a5b4fc" strokeWidth="1" opacity="0.5" strokeDasharray="3 3" />
          </g>
        )}

        {/* 5. Speed streaks for fast / kinetic dash */}
        {mascotMotion.type === 'speed-dash' && (
          <g className="vibe-block-art-speed-streaks">
            <line x1="16" y1="30" x2="38" y2="30" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" opacity="0.8" />
            <line x1="10" y1="38" x2="42" y2="38" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" opacity="0.95" />
            <line x1="18" y1="46" x2="36" y2="46" stroke="#c084fc" strokeWidth="1.5" strokeLinecap="round" opacity="0.75" />
          </g>
        )}

        {/* 6. Cloud base for float-smooth */}
        {mascotMotion.type === 'float-smooth' && (
          <g className="vibe-block-art-cloud-base">
            <ellipse cx="46" cy="56" rx="14" ry="5" fill="#e0e7ff" opacity="0.6" />
            <ellipse cx="72" cy="56" rx="14" ry="5" fill="#e0e7ff" opacity="0.6" />
            <ellipse cx="59" cy="54" rx="20" ry="6" fill="#ffffff" opacity="0.8" />
          </g>
        )}

        {/* 7. Scroll cue for scroll-reveal */}
        {mascotMotion.type === 'scroll-reveal' && (
          <g className="vibe-block-art-scroll-cue">
            <path d="M59 10 L59 20 M55 16 L59 20 L63 16" stroke="#818cf8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
          </g>
        )}

        {/* 8. Motion trail / bounce indicator rings */}
        {mascotMotion.type === 'hop' && (
          <g className="vibe-block-art-hop-ripple">
            <ellipse cx="59" cy="58" rx="16" ry="4" stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeDasharray="3 3" />
          </g>
        )}

        {/* 9. Spin trajectory arc */}
        {mascotMotion.type === 'spin-jump' && (
          <g className="vibe-block-art-spin-arc">
            <ellipse cx="59" cy="56" rx="20" ry="5" stroke="rgba(199, 210, 254, 0.45)" strokeWidth="1" strokeDasharray="4 2" />
          </g>
        )}

        {/* Floating magic stars */}
        <g className="vibe-block-art-star vibe-block-art-star--1">
          <path d="M22 22 L23.5 17 L25 22 L30 23.5 L25 25 L23.5 30 L22 25 L17 23.5 Z" fill="#fef08a" opacity="0.9" />
        </g>
        <g className="vibe-block-art-star vibe-block-art-star--2">
          <path d="M92 18 L93 14 L94 18 L98 19 L94 20 L93 24 L92 20 L88 19 Z" fill="#93c5fd" opacity="0.85" />
        </g>
        <g className="vibe-block-art-star vibe-block-art-star--3">
          <circle cx="86" cy="46" r="1.5" fill="#fbcfe8" opacity="0.8" />
          <circle cx="34" cy="50" r="1.2" fill="#c7d2fe" opacity="0.75" />
        </g>

        {/* Mascot / Character group with specific motion animation */}
        <g className={`vibe-block-art-mascot ${mascotMotion.className}`}>
          {/* Left ear */}
          <path d="M49 14 C48 6, 52 4, 54 11 C55 16, 53 22, 51 24 Z" fill="url(#char-body)" />
          <path d="M50 14 C49.5 9, 52 8, 53 12 C53.5 15, 52 19, 51 21 Z" fill="url(#char-ear)" opacity="0.75" />

          {/* Right ear */}
          <path d="M63 13 C64 5, 68 3, 70 10 C71 15, 69 21, 67 24 Z" fill="url(#char-body)" />
          <path d="M64 13 C64.5 8, 67 7, 68 11 C68.5 14, 67 19, 66 21 Z" fill="url(#char-ear)" opacity="0.75" />

          {/* Head & Body */}
          <ellipse cx="59" cy="31" rx="15" ry="13" fill="url(#char-body)" />
          <ellipse cx="59" cy="47" rx="18" ry="14" fill="url(#char-body)" />

          {/* Cheeks & Eyes */}
          <circle cx="53" cy="30" r="1.8" fill="#1e1b4b" />
          <circle cx="65" cy="30" r="1.8" fill="#1e1b4b" />
          <circle cx="53.6" cy="29.4" r="0.6" fill="#ffffff" />
          <circle cx="65.6" cy="29.4" r="0.6" fill="#ffffff" />
          <circle cx="49" cy="34" r="2.2" fill="#f472b6" opacity="0.6" />
          <circle cx="69" cy="34" r="2.2" fill="#f472b6" opacity="0.6" />

          {/* Nose & Mouth */}
          <ellipse cx="59" cy="33.5" rx="1.2" ry="0.8" fill="#f472b6" />
          <path d="M57.5 35 Q59 36.5 60.5 35" stroke="#6366f1" strokeWidth="0.9" strokeLinecap="round" fill="none" />

          {/* Soft paws */}
          <ellipse cx="51" cy="46" rx="3.5" ry="4.5" fill="#e0e7ff" />
          <ellipse cx="67" cy="46" rx="3.5" ry="4.5" fill="#e0e7ff" />
        </g>
      </svg>
    );
  }

  if (motif === 'scene' || motion === 'parallax') {
    return (
      <svg
        className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--scene"
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="scene-sky" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="scene-platform" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="scene-sphere" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
        </defs>

        {/* Distant background layer (slow drift) */}
        <g className="vibe-block-art-parallax-back">
          <circle cx="86" cy="18" r="9" fill="#f8fafc" opacity="0.9" />
          <circle cx="86" cy="18" r="14" fill="#38bdf8" opacity="0.18" />
          {/* Distant mountains/waves */}
          <path d="M6 38 Q28 26 50 36 T96 32 T118 38 L118 69 L6 69 Z" fill="url(#scene-sky)" />
          <circle cx="28" cy="14" r="1" fill="#bae6fd" opacity="0.8" />
          <circle cx="44" cy="18" r="1.2" fill="#bae6fd" opacity="0.8" />
        </g>

        {/* Middle ground isometric platform */}
        <g className="vibe-block-art-parallax-mid">
          {/* Platform shadow */}
          <ellipse cx="59" cy="53" rx="36" ry="11" fill="#020617" opacity="0.35" />
          {/* Isometric glass plane */}
          <polygon points="59,34 94,45 59,56 24,45" fill="url(#scene-platform)" stroke="rgba(255,255,255,0.7)" strokeWidth="0.8" />
          <polygon points="59,40 84,48 59,54 34,48" fill="rgba(255,255,255,0.18)" />
          {/* Grid lines on platform */}
          <line x1="41" y1="40" x2="76" y2="51" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
          <line x1="77" y1="40" x2="42" y2="51" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
        </g>

        {/* Foreground floating 3D sphere & ring */}
        <g className="vibe-block-art-parallax-fore">
          <ellipse cx="59" cy="44" rx="8" ry="3" fill="#0f172a" opacity="0.4" />
          <circle cx="59" cy="30" r="9" fill="url(#scene-sphere)" />
          <circle cx="56" cy="27" r="3" fill="#ffffff" opacity="0.75" />
          {/* Orbital ring */}
          <ellipse cx="59" cy="30" rx="15" ry="5" stroke="#38bdf8" strokeWidth="1" opacity="0.85" transform="rotate(-15 59 30)" />
        </g>
      </svg>
    );
  }

  if (motif === 'particles' || motion === 'particles') {
    if (safeIndex === 1) {
      // Cosmic vortex spiral variant
      return (
        <svg className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--particles-vortex" viewBox="0 0 118 69" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="59" cy="34" r="26" fill="#818cf8" opacity="0.15" />
          <g className="vibe-block-art-vortex">
            <path d="M59 14 A20 20 0 0 1 79 34 A20 20 0 0 1 59 54" stroke="#67e8f9" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3 3" opacity="0.8" />
            <path d="M59 20 A14 14 0 0 1 73 34 A14 14 0 0 1 59 48" stroke="#a78bfa" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
            <circle cx="73" cy="34" r="2.8" fill="#fef08a" />
            <circle cx="45" cy="34" r="2.2" fill="#67e8f9" />
            <circle cx="59" cy="20" r="2" fill="#f472b6" />
          </g>
          <circle cx="59" cy="34" r="4.5" fill="#ffffff" />
        </svg>
      );
    }

    if (safeIndex === 2) {
      // Rising fireflies variant
      return (
        <svg className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--particles-fireflies" viewBox="0 0 118 69" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <ellipse cx="59" cy="58" rx="42" ry="8" fill="#0f172a" opacity="0.3" />
          <g className="vibe-block-art-firefly vibe-block-art-firefly--1">
            <circle cx="36" cy="42" r="3" fill="#67e8f9" />
            <circle cx="36" cy="42" r="6" fill="#67e8f9" opacity="0.3" />
          </g>
          <g className="vibe-block-art-firefly vibe-block-art-firefly--2">
            <circle cx="59" cy="36" r="3.5" fill="#fef08a" />
            <circle cx="59" cy="36" r="7" fill="#fef08a" opacity="0.3" />
          </g>
          <g className="vibe-block-art-firefly vibe-block-art-firefly--3">
            <circle cx="82" cy="44" r="2.8" fill="#a78bfa" />
            <circle cx="82" cy="44" r="5.5" fill="#a78bfa" opacity="0.3" />
          </g>
        </svg>
      );
    }

    // Default constellation
    return (
      <svg
        className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--particles"
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="nebula-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.4" />
            <stop offset="60%" stopColor="#818cf8" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Nebula core */}
        <ellipse cx="59" cy="34" rx="38" ry="24" fill="url(#nebula-core)" />

        {/* Constellation lines */}
        <path
          d="M24 38 L38 22 L59 28 L80 18 L94 36 L76 48 L44 46 Z"
          stroke="rgba(255,255,255,0.22)"
          strokeWidth="0.6"
          strokeDasharray="2 2"
        />

        {/* Pulsing particles */}
        <g className="vibe-block-art-node vibe-block-art-node--1">
          <circle cx="38" cy="22" r="3.2" fill="#2dd4bf" />
          <circle cx="38" cy="22" r="6" fill="#2dd4bf" opacity="0.3" />
        </g>
        <g className="vibe-block-art-node vibe-block-art-node--2">
          <circle cx="59" cy="28" r="4.5" fill="#67e8f9" />
          <circle cx="59" cy="28" r="8" fill="#67e8f9" opacity="0.35" />
          <circle cx="57.5" cy="26.5" r="1.2" fill="#ffffff" />
        </g>
        <g className="vibe-block-art-node vibe-block-art-node--3">
          <circle cx="80" cy="18" r="3" fill="#a78bfa" />
          <circle cx="80" cy="18" r="5.5" fill="#a78bfa" opacity="0.3" />
        </g>
        <g className="vibe-block-art-node vibe-block-art-node--4">
          <circle cx="94" cy="36" r="3.5" fill="#38bdf8" />
          <circle cx="94" cy="36" r="6.5" fill="#38bdf8" opacity="0.3" />
        </g>
        <g className="vibe-block-art-node vibe-block-art-node--5">
          <circle cx="76" cy="48" r="2.8" fill="#f472b6" />
          <circle cx="76" cy="48" r="5" fill="#f472b6" opacity="0.28" />
        </g>
        <g className="vibe-block-art-node vibe-block-art-node--6">
          <circle cx="44" cy="46" r="3" fill="#34d399" />
        </g>
        <g className="vibe-block-art-node vibe-block-art-node--7">
          <circle cx="24" cy="38" r="2.4" fill="#a5b4fc" />
        </g>

        {/* Ambient sparkle motes */}
        <circle cx="30" cy="15" r="1.2" fill="#ffffff" opacity="0.9" />
        <circle cx="68" cy="42" r="1.2" fill="#ffffff" opacity="0.9" />
        <circle cx="88" cy="46" r="1" fill="#ffffff" opacity="0.75" />
      </svg>
    );
  }

  if (motif === 'cards' || layout === 'stack') {
    if (safeIndex === 1 || layout === 'grid') {
      // 2x2 Grid of modern mini cards
      return (
        <svg className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--cards-grid" viewBox="0 0 118 69" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="18" y="11" width="38" height="21" rx="4" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" className="vibe-block-art-grid-item vibe-block-art-grid-item--1" />
          <rect x="23" y="16" width="16" height="3" rx="1.5" fill="#6366f1" />
          <rect x="23" y="22" width="26" height="2" rx="1" fill="#cbd5e1" />

          <rect x="62" y="11" width="38" height="21" rx="4" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" className="vibe-block-art-grid-item vibe-block-art-grid-item--2" />
          <rect x="67" y="16" width="16" height="3" rx="1.5" fill="#8b5cf6" />
          <rect x="67" y="22" width="26" height="2" rx="1" fill="#cbd5e1" />

          <rect x="18" y="37" width="38" height="21" rx="4" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" className="vibe-block-art-grid-item vibe-block-art-grid-item--3" />
          <rect x="23" y="42" width="16" height="3" rx="1.5" fill="#06b6d4" />
          <rect x="23" y="48" width="26" height="2" rx="1" fill="#cbd5e1" />

          <rect x="62" y="37" width="38" height="21" rx="4" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" className="vibe-block-art-grid-item vibe-block-art-grid-item--4" />
          <rect x="67" y="42" width="16" height="3" rx="1.5" fill="#ec4899" />
          <rect x="67" y="48" width="26" height="2" rx="1" fill="#cbd5e1" />
        </svg>
      );
    }

    if (safeIndex === 2 || layout === 'carousel') {
      // Sliding Carousel Cards with navigation
      return (
        <svg className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--cards-carousel" viewBox="0 0 118 69" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <g className="vibe-block-art-carousel-track">
            <rect x="6" y="14" width="32" height="40" rx="5" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" opacity="0.6" />
            <rect x="44" y="12" width="34" height="44" rx="6" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
            <rect x="50" y="18" width="22" height="14" rx="3" fill="#6366f1" opacity="0.9" />
            <rect x="50" y="36" width="22" height="3" rx="1.5" fill="#1e293b" />
            <rect x="50" y="42" width="16" height="2" rx="1" fill="#94a3b8" />
            <rect x="84" y="14" width="32" height="40" rx="5" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" opacity="0.6" />
          </g>
          <circle cx="53" cy="62" r="1.5" fill="#6366f1" />
          <circle cx="59" cy="62" r="1.5" fill="#cbd5e1" />
          <circle cx="65" cy="62" r="1.5" fill="#cbd5e1" />
        </svg>
      );
    }

    // Default: 3D Stack
    return (
      <svg
        className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--cards"
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="card-grad-back" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#475569" />
            <stop offset="100%" stopColor="#334155" />
          </linearGradient>
          <linearGradient id="card-grad-mid" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#4f46e5" />
          </linearGradient>
          <linearGradient id="card-grad-front" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#f1f5f9" />
          </linearGradient>
        </defs>

        {/* Shadow */}
        <ellipse cx="60" cy="58" rx="34" ry="7" fill="#020617" opacity="0.4" />

        {/* Back card */}
        <g className="vibe-block-art-card vibe-block-art-card--back">
          <rect x="24" y="14" width="46" height="34" rx="6" fill="url(#card-grad-back)" opacity="0.7" transform="rotate(-12 47 31)" />
        </g>

        {/* Middle card */}
        <g className="vibe-block-art-card vibe-block-art-card--mid">
          <rect x="36" y="16" width="48" height="35" rx="6" fill="url(#card-grad-mid)" transform="rotate(-3 60 33)" />
          <rect x="42" y="24" width="20" height="3" rx="1.5" fill="#ffffff" opacity="0.7" transform="rotate(-3 60 33)" />
          <rect x="42" y="30" width="30" height="2.5" rx="1.25" fill="#ffffff" opacity="0.5" transform="rotate(-3 60 33)" />
        </g>

        {/* Front hero card */}
        <g className="vibe-block-art-card vibe-block-art-card--front">
          <rect x="44" y="18" width="50" height="37" rx="6" fill="url(#card-grad-front)" stroke="#e2e8f0" strokeWidth="0.8" transform="rotate(6 69 36)" />
          {/* Card header accent */}
          <rect x="50" y="24" width="16" height="12" rx="3" fill="#6366f1" transform="rotate(6 69 36)" />
          <circle cx="58" cy="30" r="3" fill="#ffffff" opacity="0.8" transform="rotate(6 69 36)" />
          {/* Text lines */}
          <rect x="70" y="25" width="18" height="3" rx="1.5" fill="#1e293b" transform="rotate(6 69 36)" />
          <rect x="70" y="31" width="12" height="2" rx="1" fill="#64748b" transform="rotate(6 69 36)" />
          <rect x="50" y="42" width="36" height="4" rx="2" fill="#e2e8f0" transform="rotate(6 69 36)" />
        </g>
      </svg>
    );
  }

  if (motif === 'gallery' || layout === 'carousel') {
    return (
      <svg
        className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--gallery"
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="gallery-img" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#818cf8" />
          </linearGradient>
        </defs>

        {/* Left partial card */}
        <rect x="6" y="15" width="22" height="38" rx="4" fill="#ffffff" opacity="0.4" stroke="#cbd5e1" strokeWidth="0.6" />

        {/* Center hero card */}
        <g className="vibe-block-art-gallery-hero">
          <rect x="34" y="11" width="50" height="44" rx="6" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" />
          {/* Image area */}
          <rect x="38" y="15" width="42" height="24" rx="4" fill="url(#gallery-img)" />
          <circle cx="48" cy="23" r="3" fill="#ffffff" opacity="0.85" />
          <path d="M42 34 L54 26 L64 34 L72 29 L80 34 L42 34 Z" fill="#ffffff" opacity="0.75" />
          {/* Caption lines */}
          <rect x="38" y="43" width="22" height="3" rx="1.5" fill="#1e293b" />
          <rect x="38" y="48" width="14" height="2" rx="1" fill="#94a3b8" />
        </g>

        {/* Right partial card */}
        <rect x="90" y="15" width="22" height="38" rx="4" fill="#ffffff" opacity="0.4" stroke="#cbd5e1" strokeWidth="0.6" />

        {/* Carousel indicators */}
        <circle cx="53" cy="61" r="1.6" fill="#94a3b8" />
        <circle cx="59" cy="61" r="2.2" fill="#6366f1" />
        <circle cx="65" cy="61" r="1.6" fill="#94a3b8" />
      </svg>
    );
  }

  if (motif === 'product') {
    return (
      <svg
        className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--product"
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="prod-box" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
          <radialGradient id="prod-spot" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Spotlight & Pedestal */}
        <ellipse cx="59" cy="30" rx="30" ry="20" fill="url(#prod-spot)" />
        <ellipse cx="59" cy="54" rx="28" ry="8" fill="#18181b" opacity="0.35" />
        <ellipse cx="59" cy="52" rx="26" ry="6.5" fill="#3f3f46" stroke="#52525b" strokeWidth="0.6" />

        {/* Floating Product Hero Box */}
        <g className="vibe-block-art-product-float">
          {/* Isometric box */}
          <polygon points="59,18 76,26 59,34 42,26" fill="#fef3c7" />
          <polygon points="42,26 59,34 59,48 42,40" fill="url(#prod-box)" />
          <polygon points="59,34 76,26 76,40 59,48" fill="#d97706" />

          {/* Glowing badge */}
          <circle cx="78" cy="20" r="6" fill="#ef4444" />
          <path d="M76 20 L77.5 21.5 L80.5 18.5" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>

        {/* Sparkles */}
        <circle cx="34" cy="24" r="1.5" fill="#fef08a" />
        <circle cx="86" cy="38" r="1.2" fill="#fef08a" />
      </svg>
    );
  }

  if (motif === 'timeline') {
    return (
      <svg
        className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--timeline"
        viewBox="0 0 118 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="50%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>

        {/* Curved connecting pipeline */}
        <path
          d="M16 42 C34 42, 38 24, 59 24 C80 24, 84 42, 102 42"
          stroke="url(#line-grad)"
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />

        {/* Node 1: Completed */}
        <circle cx="20" cy="42" r="5" fill="#10b981" />
        <path d="M18.5 42 L19.5 43 L22 40.5" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />

        {/* Node 2: Active / Hero */}
        <g className="vibe-block-art-timeline-active">
          <circle cx="59" cy="24" r="8" fill="#3b82f6" opacity="0.25" />
          <circle cx="59" cy="24" r="5.5" fill="#3b82f6" />
          <circle cx="59" cy="24" r="2.5" fill="#ffffff" />
        </g>

        {/* Node 3: Upcoming */}
        <circle cx="98" cy="42" r="5" fill="#a855f7" />
        <circle cx="98" cy="42" r="2" fill="#ffffff" opacity="0.7" />

        {/* Labels below nodes */}
        <rect x="13" y="52" width="14" height="3" rx="1.5" fill="#94a3b8" />
        <rect x="52" y="36" width="14" height="3" rx="1.5" fill="#3b82f6" />
        <rect x="91" y="52" width="14" height="3" rx="1.5" fill="#94a3b8" />
      </svg>
    );
  }

  // Default: CTA / Content / Abstract / Split layout with step-based variety
  if (safeIndex === 1 || layout === 'split') {
    // Split layout
    return (
      <svg className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--cta-split" viewBox="0 0 118 69" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="10" y="12" width="44" height="45" rx="6" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="0.8" />
        <circle cx="32" cy="30" r="10" fill="#3b82f6" opacity="0.8" />
        <path d="M26 38 L38 38 M32 24 L32 36" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" />
        <rect x="60" y="16" width="48" height="4" rx="2" fill="#1e293b" />
        <rect x="60" y="24" width="40" height="2.5" rx="1.25" fill="#94a3b8" />
        <rect x="60" y="30" width="44" height="2.5" rx="1.25" fill="#cbd5e1" />
        <rect x="60" y="38" width="32" height="11" rx="4" fill="#3b82f6" />
        <rect x="66" y="42" width="16" height="2.5" rx="1.25" fill="#ffffff" />
      </svg>
    );
  }

  if (safeIndex === 2 || layout === 'canvas') {
    // Minimalist centered badge card
    return (
      <svg className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--cta-minimal" viewBox="0 0 118 69" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="22" y="12" width="74" height="45" rx="8" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" />
        <circle cx="59" cy="26" r="8" fill="#8b5cf6" opacity="0.2" />
        <circle cx="59" cy="26" r="5" fill="#8b5cf6" />
        <rect x="36" y="38" width="46" height="3.5" rx="1.75" fill="#1e293b" />
        <rect x="42" y="45" width="34" height="2.5" rx="1.25" fill="#94a3b8" />
      </svg>
    );
  }

  // Hero Card with vibrant CTA button
  return (
    <svg
      className="vibe-block-design-wizard-option-art vibe-block-design-wizard-option-art--cta"
      viewBox="0 0 118 69"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cta-btn" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4f46e5" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient id="cta-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>

      {/* Modern UI Card Mockup */}
      <rect x="12" y="10" width="94" height="49" rx="8" fill="#ffffff" stroke="#e2e8f0" strokeWidth="0.8" />

      {/* Header bar / Title */}
      <rect x="22" y="18" width="44" height="4" rx="2" fill="#1e293b" />
      <rect x="22" y="25" width="56" height="2.5" rx="1.25" fill="#94a3b8" />
      <rect x="22" y="30" width="38" height="2.5" rx="1.25" fill="#cbd5e1" />

      {/* Interactive Glowing CTA Button */}
      <g className="vibe-block-art-cta-btn">
        <rect x="22" y="38" width="36" height="12" rx="4" fill="url(#cta-btn)" />
        <rect x="27" y="42.5" width="16" height="2.5" rx="1.25" fill="#ffffff" />
        <circle cx="49" cy="44" r="1.8" fill="#ffffff" opacity="0.9" />
      </g>

      {/* Floating Accent Sphere */}
      <circle cx="86" cy="34" r="11" fill="url(#cta-accent)" />
      <circle cx="83" cy="30" r="3.5" fill="#ffffff" opacity="0.6" />
    </svg>
  );
};
